import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SSEEvent {
  type: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SseService {
  private eventSource: EventSource | null = null;
  private eventsSubject = new Subject<SSEEvent>();

  constructor(private ngZone: NgZone) {}

  /**
   * Connect to the SSE endpoint.
   *
   * @param token  Supabase access token — required because /api/events now
   *               enforces authentication.  The native EventSource API cannot
   *               send custom headers, so the token is passed as a query
   *               parameter (?token=…).  The backend validates it server-side.
   * @param teamId Optional team UUID to scope the event stream to a specific
   *               team the authenticated user belongs to.
   */
  connect(token: string, teamId?: string | null): Observable<SSEEvent> {
    if (this.eventSource) {
      this.eventSource.close();
    }

    // Create a fresh stream for each connection attempt.
    this.eventsSubject = new Subject<SSEEvent>();

    // Build the SSE URL with authentication token (and optional team scope).
    // The token is passed as a query parameter because the native EventSource
    // API does not support custom request headers.
    const params = new URLSearchParams({ token });
    if (teamId) {
      params.set('teamId', teamId);
    }
    const sseUrl = `${environment.apiUrl}/events?${params.toString()}`;

    this.eventSource = new EventSource(sseUrl);

    this.eventSource.onmessage = (event) => {
      this.ngZone.run(() => {
        try {
          const data = JSON.parse(event.data);
          this.eventsSubject.next(data);
        } catch (error) {
          console.error('Error parsing SSE data:', error);
        }
      });
    };

    this.eventSource.onerror = (error) => {
      console.error('SSE error:', error);
    };

    return this.eventsSubject.asObservable();
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
